import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

interface PdfRenderTaskLike {
  promise: Promise<void>;
  cancel(): void;
}

interface PdfViewportLike {
  width: number;
  height: number;
  scale: number;
  transform: number[];
}

interface PdfTextItemLike {
  str: string;
  transform: number[];
  width: number;
  height: number;
}

interface PdfPageLike {
  getViewport(options: { scale: number }): PdfViewportLike;
  getTextContent(): Promise<{ items: PdfTextItemLike[] }>;
  render(options: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewportLike;
    transform?: number[];
  }): PdfRenderTaskLike;
}

interface PdfDocumentLike {
  numPages: number;
  getPage(pageNumber: number): Promise<PdfPageLike>;
  destroy(): Promise<void>;
}

interface PdfJsModuleLike {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(options: Record<string, unknown>): {
    promise: Promise<PdfDocumentLike>;
  };
}

export interface AiViewerSource {
  documentId: number;
  fileName: string;
  mimeType: string;
  bytes: ArrayBuffer;
}

interface HighlightBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

function normalize(value: string): string {
  return `${value ?? ""}`
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Mostra a página do documento de onde saiu um valor.
 *
 * Em PDF com camada de texto, o excerto lido é procurado na página e realçado.
 * Em digitalizações e imagens não há texto para procurar: mostra-se a página e
 * o excerto lido por baixo.
 */
@Component({
  selector: "app-ai-doc-viewer",
  templateUrl: "./ai-doc-viewer.component.html",
  styleUrls: ["./ai-doc-viewer.component.scss"],
})
export class AiDocViewerComponent implements OnChanges, OnDestroy {
  @Input() source: AiViewerSource | null = null;
  @Input() page: number | null = null;
  @Input() snippet: string | null = null;
  @Input() loading = false;

  @ViewChild("canvas") canvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild("stage") stage?: ElementRef<HTMLDivElement>;

  isPdf = false;
  imageUrl: string | null = null;
  pageCount = 0;
  currentPage = 1;
  rendering = false;
  failed = false;
  highlighted = false;
  highlights: HighlightBox[] = [];
  viewportWidth = 0;
  viewportHeight = 0;
  zoom = 1;

  private pdfDocument: PdfDocumentLike | null = null;
  private loadedDocumentId: number | null = null;
  private renderTask: PdfRenderTaskLike | null = null;
  private renderSequence = 0;
  private destroyed = false;

  constructor(private cdr: ChangeDetectorRef) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes["source"]) {
      await this.open();
      return;
    }
    if (changes["page"] || changes["snippet"]) {
      this.currentPage = this.clampPage(this.page ?? this.currentPage);
      await this.render();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.renderTask?.cancel();
    void this.pdfDocument?.destroy();
    this.revokeImage();
  }

  async previous() {
    if (this.currentPage <= 1) return;
    this.currentPage -= 1;
    await this.render();
  }

  async next() {
    if (this.currentPage >= this.pageCount) return;
    this.currentPage += 1;
    await this.render();
  }

  async zoomBy(delta: number) {
    this.zoom = Math.min(2.5, Math.max(0.6, Number((this.zoom + delta).toFixed(2))));
    await this.render();
  }

  private clampPage(page: number): number {
    const wanted = Math.floor(Number(page) || 1);
    return Math.min(Math.max(1, wanted), Math.max(1, this.pageCount));
  }

  private revokeImage() {
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
    this.imageUrl = null;
  }

  private async open(): Promise<void> {
    this.failed = false;
    this.highlights = [];
    this.highlighted = false;

    const source = this.source;
    if (!source) {
      this.loadedDocumentId = null;
      this.pageCount = 0;
      this.revokeImage();
      return;
    }
    if (this.loadedDocumentId === source.documentId) {
      this.currentPage = this.clampPage(this.page ?? 1);
      await this.render();
      return;
    }

    this.renderTask?.cancel();
    await this.pdfDocument?.destroy();
    this.pdfDocument = null;
    this.revokeImage();
    this.loadedDocumentId = source.documentId;
    this.isPdf = source.mimeType === "application/pdf";

    try {
      if (this.isPdf) {
        const pdfjs = await this.loadPdfJs();
        const assetRoot = new URL("assets/pdfjs/", document.baseURI).toString();
        pdfjs.GlobalWorkerOptions.workerSrc = `${assetRoot}pdf.worker.mjs`;
        const task = pdfjs.getDocument({
          // O pdf.js fica com o buffer: entrega-se uma cópia.
          data: new Uint8Array(source.bytes.slice(0)),
          cMapUrl: `${assetRoot}cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `${assetRoot}standard_fonts/`,
          isEvalSupported: false,
          enableXfa: false,
        });
        this.pdfDocument = await task.promise;
        this.pageCount = this.pdfDocument.numPages;
      } else {
        this.pageCount = 1;
        this.imageUrl = URL.createObjectURL(
          new Blob([source.bytes], { type: source.mimeType }),
        );
      }
      this.currentPage = this.clampPage(this.page ?? 1);
      this.refresh();
      await this.render();
    } catch {
      this.failed = true;
      this.refresh();
    }
  }

  private loadPdfJs(): Promise<PdfJsModuleLike> {
    const moduleUrl = new URL("assets/pdfjs/pdf.mjs", document.baseURI).toString();
    return import(/* webpackIgnore: true */ moduleUrl) as Promise<PdfJsModuleLike>;
  }

  private refresh() {
    if (!this.destroyed) this.cdr.detectChanges();
  }

  private async render(): Promise<void> {
    this.highlights = [];
    this.highlighted = false;
    if (!this.isPdf || !this.pdfDocument) {
      this.refresh();
      return;
    }

    const sequence = ++this.renderSequence;
    try {
      const page = await this.pdfDocument.getPage(this.currentPage);
      if (sequence !== this.renderSequence || this.destroyed) return;

      // Ajusta à largura disponível; o zoom multiplica a partir daí.
      const base = page.getViewport({ scale: 1 });
      const available = Math.max(320, (this.stage?.nativeElement?.clientWidth ?? 640) - 24);
      const viewport = page.getViewport({
        scale: (available / base.width) * this.zoom,
      });
      this.viewportWidth = viewport.width;
      this.viewportHeight = viewport.height;
      this.refresh();

      const canvas = this.canvas?.nativeElement;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      this.renderTask?.cancel();
      this.rendering = true;
      const task = page.render({
        canvasContext: context,
        viewport,
        transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
      });
      this.renderTask = task;
      await task.promise;
      if (sequence !== this.renderSequence) return;

      this.highlights = await this.findHighlights(page, viewport);
      this.highlighted = this.highlights.length > 0;
      if (this.highlighted) this.scrollToHighlight();
    } catch (error) {
      if ((error as { name?: string })?.name !== "RenderingCancelledException") {
        this.failed = true;
      }
    } finally {
      if (sequence === this.renderSequence) {
        this.rendering = false;
        this.refresh();
      }
    }
  }

  /**
   * Procura o excerto na camada de texto da página e devolve as caixas dos
   * pedaços de texto que o compõem. Sem camada de texto não devolve nada.
   */
  private async findHighlights(
    page: PdfPageLike,
    viewport: PdfViewportLike,
  ): Promise<HighlightBox[]> {
    const wanted = normalize(this.snippet ?? "");
    if (wanted.length < 3) return [];

    const content = await page.getTextContent();
    const items = (content?.items ?? []).filter((item) => !!`${item?.str ?? ""}`.trim());
    if (!items.length) return [];

    let text = "";
    const ranges: Array<{ start: number; end: number; item: PdfTextItemLike }> = [];
    for (const item of items) {
      const piece = normalize(item.str);
      if (!piece) continue;
      if (text) text += " ";
      ranges.push({ start: text.length, end: text.length + piece.length, item });
      text += piece;
    }

    // O excerto pode ter pequenas diferenças: tenta-se inteiro e depois só o
    // início, que costuma chegar para localizar a linha.
    const candidates = [wanted, wanted.slice(0, 60), wanted.slice(0, 30), wanted.slice(0, 16)];
    let start = -1;
    let length = 0;
    for (const candidate of candidates) {
      if (candidate.length < 3) continue;
      start = text.indexOf(candidate);
      if (start >= 0) {
        length = candidate.length;
        break;
      }
    }
    if (start < 0) return [];

    const end = start + length;
    const m = viewport.transform;
    return ranges
      .filter((range) => range.start < end && range.end > start)
      .slice(0, 40)
      .map((range) => {
        const t = range.item.transform;
        // Matriz do texto multiplicada pela do viewport.
        const a = m[0] * t[0] + m[2] * t[1];
        const b = m[1] * t[0] + m[3] * t[1];
        const c = m[0] * t[2] + m[2] * t[3];
        const d = m[1] * t[2] + m[3] * t[3];
        const e = m[0] * t[4] + m[2] * t[5] + m[4];
        const f = m[1] * t[4] + m[3] * t[5] + m[5];
        const height = Math.max(8, Math.hypot(c, d));
        const width = Math.max(8, range.item.width * viewport.scale);
        const angle = Math.atan2(b, a);
        // Texto rodado: a caixa deixa de ser fiável, fica de fora.
        if (Math.abs(angle) > 0.2) return null;
        return { left: e - 2, top: f - height - 1, width: width + 4, height: height + 4 };
      })
      .filter((box): box is HighlightBox => !!box);
  }

  private scrollToHighlight() {
    const stage = this.stage?.nativeElement;
    const first = this.highlights[0];
    if (!stage || !first) return;
    stage.scrollTo({ top: Math.max(0, first.top - 120), behavior: "smooth" });
  }
}
